import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useGetAuthToken } from '~/hooks/useGetAuthToken';
import { COMPARATOR_TYPES, ContentsApiResponse, searchApi } from '~/searchApi';

export const useContentWithMetrics = (
  contentIds: string[],
  showInShared: boolean,
  enabled: boolean = true,
  slug?: string,
) => {
  const router = useRouter();
  const authToken = useGetAuthToken({ creatorSlug: slug });

  // Determine if we need to use a share token
  const isSharedPage = router.pathname.includes('/shared/');
  const shareToken =
    isSharedPage && authToken && typeof authToken === 'string'
      ? authToken
      : undefined;

  // Create a stable, order-independent list of IDs for the query key and fetching
  const stableIds = Array.from(new Set(contentIds)).sort();

  return useQuery(
    ['content-with-metrics', stableIds, showInShared, shareToken],
    async () => {
      if (stableIds.length === 0) {
        return [];
      }

      // Fetch each content individually to get full metrics
      const contentPromises = stableIds.map(async (contentId) => {
        try {
          const response = await searchApi.getContents(
            {
              filters: [
                {
                  comparator: COMPARATOR_TYPES.EQ,
                  name: 'id',
                  value: contentId,
                },
              ],
            },
            shareToken,
          );
          return response.data[0]; // Return the first (and only) result
        } catch (error) {
          // Silently handle errors and return null
          return null;
        }
      });

      const results = await Promise.all(contentPromises);

      // Use unknown first to avoid type issues, then cast to the expected type
      return results.filter(
        Boolean,
      ) as unknown as ContentsApiResponse['results'];
    },
    {
      enabled: enabled && stableIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      keepPreviousData: true,
    },
  );
};
